import * as React from 'react';
import Autocomplete, {autocompleteClasses} from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import {useTheme, styled} from '@mui/material/styles';
import {VariableSizeList, ListChildComponentProps} from 'react-window';
import Typography from '@mui/material/Typography';
import MuiSvgIcon from '@new-components/atoms/MuiSvgIcon';
import MuiTextField, {TextFieldProps} from '@new-components/atoms/MuiTextField';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const {data, index, style} = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };
  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component='div' style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const {key, ...optionProps} = dataSet[0];

  return (
    <Typography
      key={key}
      component='li'
      {...optionProps}
      noWrap
      style={inlineStyle}>
      {dataSet[1]}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const {children, ...other} = props;
  const itemData: React.ReactElement<unknown>[] = [];
  (children as React.ReactElement<unknown>[]).forEach(
    (
      item: React.ReactElement<unknown> & {
        children?: React.ReactElement<unknown>[];
      }
    ) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactElement<unknown>) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}>
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    'boxSizing': 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

type Props = {
  options: string[];
  hide?: boolean;
  onChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
} & Pick<
  TextFieldProps,
  'id' | 'label' | 'helperText' | 'error' | 'name' | 'disabled'
>;
export function VirtualizedSelectInput(props: Props) {
  if (props.hide) return <></>;
  return (
    <Autocomplete
      id={props.id}
      fullWidth
      disableListWrap
      options={props.options}
      disabled={props.disabled}
      onChange={(event: any, newValue: string | null) => {
        if (props.onChange) props.onChange(newValue === null ? '' : newValue);
      }}
      value={props.value === '' ? null : props.value}
      defaultValue={props.defaultValue === '' ? null : props.defaultValue}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={props.label}
          helperText={!props.helperText ? ' ' : props.helperText}
          error={props.error}
          name={props.name}
          variant='outlined'
          fullWidth
          sx={{
            ['& .MuiOutlinedInput-root']: {
              fieldset: {
                borderColor: '#BABAC8',
                borderRadius: '12px',
                boxShadow: '0px 1px 2px 0px #1018280D',
                top: 0,
                legend: {
                  display: 'none',
                },
              },
            },
          }}
          InputLabelProps={{
            shrink: true,
            sx: {
              fontSize: '15px',
              fontWeight: 500,
              lineHeight: '18.75px',
              letterSpacing: '-0.4000000059604645px',
              textAlign: 'left',
              textUnderlinePosition: 'fromF-font',
              textDecorationSkipInk: 'none',
              color: '#82807E',
              transform: 'none',
              position: 'static',
              marginBottom: '6px',
              '&.Mui-error': {
                color: 'initial',
              },
            },
          }}
          inputProps={{
            ...params.inputProps,
            sx: {
              padding: '0px !important',
            },
          }}
        />
      )}
      renderOption={(props, option, state) =>
        [props, option, state.index] as React.ReactNode
      }
      popupIcon={
        <MuiSvgIcon>
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M5 7.5L10 12.5L15 7.5'
              stroke='#98A2B3'
              strokeWidth='1.66667'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </MuiSvgIcon>
      }
      // renderGroup={(params) => params as any}
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
    />
  );
}
